import { useCallback, useEffect, useState, useRef } from "react";
import useDarkMode from '../../hooks/useDarkMode';
import useThemeColor from "../../hooks/useThemeColor";
import { useSelector, useDispatch } from "react-redux";
import Select from "react-select";
import swal from "sweetalert";
import showErrorToast, { showSuccessToast } from "../../components/CustomComponents/toast/toast";
import Preloader from "../../components/CustomComponents/preLoad/preLoader";
import ColumnSelection from "../../components/CustomComponents/ColumnSelection/ColumnSelection";
import DropDownStyles, { DropDownStylesDark } from "../staff/dropdownStyles";
import { SortOrderDropDownSelection } from "../../components/CustomComponents/DropdownSelections/DropDownSelections";
import axiosInstance from "../../config/axiosApi/axiosApi";
import APIs from "../../Constants/apis";
import { setVisibleColumns, setSortOrder, setSortBy } from "../../app/itemGridPreferencesSlice";
import { mergeVisibleColumns, needsPreferencesUpdate } from "../../utils/preferencesUtils";
import { MANAGE_PAGE_SEARCH_DEBOUNCE_MS } from "../../utils/managePageConstants";
import CategoryTypeModel from "./add&updateCategoryType";
import { appSettings } from "../../app/appSettingsSlice";
import useIsChildBranch from "../../hooks/useIsChildBranch";
import "./../order/ManageOrderFilters.css";

const ManageCategoryTypes = () => {
  const compID = useSelector((state) => state.auth.user).userData?.compID;
  const [search, setSearch] = useState("");
  const pageKey = "reportingCategories";
  const defaultVisibleColumns = {
    sortOrder: true,
  };
  const defaultSortOrder = { value: "DESC", label: "DESC" };
  const defaultSortBy = { value: "rC_ID", label: "ID" };
  const preferences = useSelector((state) => state.itemGridPreferences[pageKey] || {});
  const visibleColumns = mergeVisibleColumns(preferences.visibleColumns, defaultVisibleColumns);
  const sortOrder = preferences.sortOrder || defaultSortOrder;
  const sortBy = preferences.sortBy || defaultSortBy;
  const dispatch = useDispatch();

  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddCategoryModal, setShowAddCategoryModal] = useState(false);
  const [categoryToEdit, setCategoryToEdit] = useState(null);
  const [selectedItems, setSelectedItems] = useState(new Set());
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const lastSelectedIndex = useRef(null);
  const dragItem = useRef(null);
  const dragOverItem = useRef(null);

  // Get app settings from Redux
  const appSettings = useSelector((state) => state.appSettings);
  const { appTheme } = appSettings;

  // Check if dark mode is enabled
  const isDarkMode = useDarkMode();
  const dropdownStyles = isDarkMode ? DropDownStylesDark : DropDownStyles;

  // Get theme color from centralized hook
  const themeColor = useThemeColor();
  const isChildBranch = useIsChildBranch();



  const sortByOptions = [
    { value: "rC_ID", label: "ID" },
    { value: "rC_Name", label: "Name" },
    { value: "rC_SortOrder", label: "Sort Order" },
  ];

  const clearFilters = () => {
    setSearch("");
  };

  const fetchCategories = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get(APIs.GET_ALL_CATEGORY_TYPES, {
        params: {
          compID,
          Search: search || "",
          SortBy: sortBy.value,
          SortOrder: sortOrder.value,
        },
      });

      const categoryData = response.data.data || [];
      if (Array.isArray(categoryData)) {
        setCategories(categoryData);
      } else {
        // Unexpected API response format silently
        setCategories([]);
      }
    } catch (error) {
      // Error fetching categories silently
      setCategories([]);
      showErrorToast("Failed to fetch reporting types.");
    } finally {
      setLoading(false);
    }
  }, [compID, search, sortBy, sortOrder]);

  useEffect(() => {
    const handler = setTimeout(() => {
      fetchCategories();
    }, MANAGE_PAGE_SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(handler);
  }, [fetchCategories]);

  useEffect(() => {
    // Check if preferences exist and merge with defaults to ensure new columns are included
    if (!preferences.visibleColumns) {
      dispatch(setVisibleColumns({ pageKey, columns: defaultVisibleColumns }));
    } else {
      // Check if preferences need updating (missing new columns or have removed columns)
      if (needsPreferencesUpdate(preferences.visibleColumns, defaultVisibleColumns)) {
        // Merge existing preferences with default columns (this handles both additions and removals)
        const mergedColumns = mergeVisibleColumns(preferences.visibleColumns, defaultVisibleColumns);
        dispatch(setVisibleColumns({ pageKey, columns: mergedColumns }));
      }
    }

    if (!preferences.sortOrder) {
      dispatch(setSortOrder({ pageKey, sortOrder: defaultSortOrder }));
    }
    if (!preferences.sortBy) {
      dispatch(setSortBy({ pageKey, sortBy: defaultSortBy }));
    }
  }, [dispatch, pageKey]);

  const handleCategorySubmit = async (categoryData, isUpdate) => {
    try {
      setLoading(true);
      const url = isUpdate
        ? APIs.PUT_UPDATE_CATEGORY_TYPE
        : APIs.POST_ADD_CATEGORY_TYPE;
      const method = isUpdate ? "put" : "post";
      const response = await axiosInstance[method](url, {
        ...categoryData,
        compID,
      });
      if (response.data.status) {
        const updatedCategory = response.data.data;
        if (isUpdate) {
          setCategories((prev) =>
            prev.map((cat) =>
              cat.rC_ID === updatedCategory.rC_ID ? updatedCategory : cat
            )
          );
        } else {
          setCategories((prev) => [...prev, updatedCategory]);
        }
        showSuccessToast(`Category Type ${isUpdate ? "Updated" : "Added"}.`);
      } else {
        // API Error silently
        showErrorToast(response.data.message || "Operation failed.");
      }
    } catch (error) {
      // Error updating/adding category silently
      showErrorToast("Something went wrong.");
    } finally {
      setLoading(false);
    }
  };

  const handleAddCategoryClick = () => {
    setCategoryToEdit(null);
    setShowAddCategoryModal(true);
  };

  const handleEditCategoryClick = (category) => {
    setCategoryToEdit(category);
    setShowAddCategoryModal(true);
  };

  const DeleteCategory = async (category) => {
    const confirmDelete = await swal({
      title: "Are you sure?",
      text: `Are you sure you want to delete ${category.rC_Name}?`,
      icon: "warning",
      buttons: true,
      dangerMode: true,
    });

    if (!confirmDelete) return;

    setLoading(true);
    try {
      const response = await axiosInstance.delete(APIs.DELETE_CATEGORY_TYPE, {
        params: {
          compID,
          RC_ID: category.rC_ID,
        },
      });
      if (response.data.status) {
        setCategories((prev) =>
          prev.filter((cat) => cat.rC_ID !== category.rC_ID)
        );
        showSuccessToast("Category Type is Deleted.");
      } else {
        showErrorToast(response.data.message);
      }
    } catch (err) {
      showErrorToast("Something went wrong.");
      // Error silently
    } finally {
      setLoading(false);
    }
  };

  const handleSelectItem = (categoryId, index, event) => {
    setSelectedItems((prev) => {
      const newSelected = new Set(prev);
      if (event.shiftKey && lastSelectedIndex.current !== null) {
        const start = Math.min(lastSelectedIndex.current, index);
        const end = Math.max(lastSelectedIndex.current, index);
        for (let i = start; i <= end; i++) {
          if (categories[i]?.rC_ID) {
            newSelected.add(categories[i].rC_ID);
          }
        }
      } else {
        if (newSelected.has(categoryId)) {
          newSelected.delete(categoryId);
        } else {
          newSelected.add(categoryId);
        }
        lastSelectedIndex.current = index;
      }
      return newSelected;
    });
  };

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      const newSelected = new Set(categories.map((item) => item.rC_ID));
      setSelectedItems(newSelected);
      lastSelectedIndex.current = null;
    } else {
      setSelectedItems(new Set());
      lastSelectedIndex.current = null;
    }
  };

  const handleDeselectAll = () => {
    setSelectedItems(new Set());
    lastSelectedIndex.current = null;
  };

  const handleBulkDelete = async () => {
    if (selectedItems.size === 0) {
      showErrorToast("No categories selected for deletion.");
      return;
    }

    try {
      const confirmDelete = await swal({
        title: "Are you sure?",
        text: `Are you sure you want to delete ${selectedItems.size} selected categories?`,
        icon: "warning",
        buttons: true,
        dangerMode: true,
      });

      if (!confirmDelete) return;

      setLoading(true);
      await Promise.all(
        Array.from(selectedItems).map((categoryId) =>
          axiosInstance.delete(APIs.DELETE_CATEGORY_TYPE, {
            params: {
              compID,
              rC_ID: categoryId,
            },
          })
        )
      );

      setSelectedItems(new Set());
      lastSelectedIndex.current = null;
      fetchCategories();
      showSuccessToast(`${selectedItems.size} categories deleted successfully.`);
    } catch (err) {
      // Error in bulk delete silently
      showErrorToast("Failed to delete selected categories.");
    } finally {
      setLoading(false);
    }
  };

  const handleBulkEdit = () => {
    if (selectedItems.size === 0) {
      showErrorToast("No categories selected for editing.");
      return;
    }

    const selectedCategories = categories.filter((cat) =>
      selectedItems.has(cat.rC_ID)
    );
    setCategoryToEdit(selectedCategories);
    setShowAddCategoryModal(true);
  };

  const handleDragStart = (e, category) => {
    const index = categories.findIndex((c) => c.rC_ID === category.rC_ID);
    dragItem.current = index;
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("application/json", JSON.stringify(index));
    e.currentTarget.style.opacity = "0.5";
  };

  const handleDragEnter = (e, index) => {
    e.preventDefault();
    dragOverItem.current = index;
    e.currentTarget.style.backgroundColor = "#e9ecef";
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.currentTarget.style.backgroundColor = "";
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const handleDrop = async (e) => {
    e.preventDefault();

    const draggedIndex = JSON.parse(e.dataTransfer.getData("application/json"));
    const overIndex = dragOverItem.current;

    if (
      draggedIndex === overIndex ||
      draggedIndex === undefined ||
      overIndex === undefined
    ) {
      dragItem.current = null;
      dragOverItem.current = null;
      return;
    }

    let newCategories = [...categories];
    const draggedCategory = newCategories[draggedIndex];
    newCategories.splice(draggedIndex, 1);
    newCategories.splice(overIndex, 0, draggedCategory);
    newCategories = newCategories.map((cat, index) => ({
      ...cat,
      rC_SortOrder: index + 1,
    }));

    setCategories(newCategories);

    try {
      await axiosInstance.post(APIs.POST_UPDATE_CATEGORY_TYPE_SORT_ORDER, {
        compID: compID,
        Updates: newCategories.map((cat) => ({
          rC_ID: cat.rC_ID,
          rC_SortOrder: cat.rC_SortOrder,
        })),
      });
      fetchCategories();
      showSuccessToast("Category order updated successfully.");
    } catch (error) {
      // Error updating category order silently
      showErrorToast("Failed to update category order.");
      fetchCategories();
    }

    dragItem.current = null;
    dragOverItem.current = null;
  };

  const handleDragEnd = (e) => {
    e.currentTarget.style.opacity = "1";
    document.querySelectorAll("tr").forEach((tr) => {
      tr.style.backgroundColor = "";
    });
    dragItem.current = null;
    dragOverItem.current = null;
  };

  return (
    <div>
      {loading && <Preloader />}
      <div className="d-flex align-items-center mb-3">
        <div>
          <ol className="breadcrumb">
            <li className="breadcrumb-item">
              <a href="#/">Home</a>
            </li>
            <li className="breadcrumb-item active">Category Types</li>
          </ol>
          <h1 className="page-header mb-0">Category Types</h1>
        </div>
        <div className="ms-auto">
          {!isChildBranch && (
            <button
              className="btn px-4"
              onClick={handleAddCategoryClick}
              style={{
                backgroundColor: themeColor,
                background: `linear-gradient(135deg, ${themeColor} 0%, ${themeColor}dd 100%)`,
                border: 'none',
                color: 'white',
                borderRadius: '8px',
                padding: '8px 16px',
                fontSize: '14px',
                fontWeight: '600',
                transition: 'all 0.3s ease',
                boxShadow: `0 2px 8px ${themeColor}40`
              }}
              onMouseEnter={(e) => {
                e.target.style.background = `linear-gradient(135deg, ${themeColor}dd 0%, ${themeColor} 100%)`;
                e.target.style.transform = 'translateY(-1px)';
                e.target.style.boxShadow = `0 4px 12px ${themeColor}60`;
              }}
              onMouseLeave={(e) => {
                e.target.style.background = `linear-gradient(135deg, ${themeColor} 0%, ${themeColor}dd 100%)`;
                e.target.style.transform = 'translateY(0)';
                e.target.style.boxShadow = `0 2px 8px ${themeColor}40`;
              }}
            >
              <i className="fa fa-plus fa-lg me-2 ms-n2 text-white"></i>
              Create Category Type
            </button>
          )}
        </div>
      </div>

      <div className="mb-3 mt-2">
        <div className="card border-0">
          <div className="tab-content p-3">
            <div className="tab-pane fade show active" id="allTab">
              {/* Enhanced Filter Section */}
              <div className="mb-4">
                <div
                  className="card filter-card"
                  style={{
                    position: "relative",
                    zIndex: 1,
                    background: `linear-gradient(135deg, ${themeColor} 0%, ${themeColor}dd 100%)`,
                    borderRadius: '12px',
                    border: 'none',
                    boxShadow: `0 4px 20px ${themeColor}40`
                  }}
                >
                  <div className="card-body p-3">
                    {/* Filter Controls */}
                    <div className="row g-3 align-items-center">
                      {/* Search Field */}
                      <div className="col-xl-4 col-lg-4 col-md-6">
                        <div className="search-input filter-control">
                          <input
                            type="text"
                            className="filter-input"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            placeholder="Search by name..."
                              style={{
                                backgroundColor: 'rgba(255, 255, 255, 0.9)',
                                border: '1px solid rgba(255, 255, 255, 0.3)',
                                color: '#333'
                              }}
                          />
                        </div>
                      </div>

                      {/* Sort By Dropdown */}
                      <div className="col-xl-3 col-lg-3 col-md-6">
                        <div className="filter-control">
                          <Select
                            value={sortBy}
                            onChange={(val) => dispatch(setSortBy({ pageKey, sortBy: val }))}
                            options={sortByOptions}
                            styles={dropdownStyles}
                            placeholder="Sort By"
                            className="filter-dropdown"
                            menuPortalTarget={document.body}
                            menuPosition="fixed"
                            menuShouldBlockScroll={false}
                            menuShouldScrollIntoView={false}
                            closeMenuOnScroll={false}
                          />
                        </div>
                      </div>

                      {/* Sort Order Dropdown */}
                      <div className="col-xl-2 col-lg-2 col-md-6">
                        <div className="filter-control">
                          <SortOrderDropDownSelection
                            defaultValue={sortOrder}
                            onChange={(val) => dispatch(setSortOrder({ pageKey, sortOrder: val }))}
                          />
                        </div>
                      </div>

                      {/* Action Buttons */}
                      <div className="col-xl-3 col-lg-3 col-md-12">
                        <div className="d-flex gap-2">
                          <button
                            className="clear-btn"
                            onClick={clearFilters}
                            title="Clear all filters"
                          >
                            <i className="fa fa-times me-1"></i>
                            Filter
                          </button>

                          <ColumnSelection
                            visibleColumns={visibleColumns}
                            setVisibleColumns={(cols) =>
                              dispatch(setVisibleColumns({ pageKey, columns: cols }))
                            }
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Selection Info and Actions (Shown only when categories are selected) */}
              {selectedItems.size > 0 && (
                <div className="mb-3">
                  <div className="p-3 bg-light rounded d-flex align-items-center justify-content-between" style={{ border: "1px solid #dee2e6" }}>
                    <div className="d-flex align-items-center gap-3">
                      <span className="fw-bold text-primary">
                        {selectedItems.size} categor{selectedItems.size > 1 ? 'ies' : 'y'} selected
                      </span>
                      <button
                        className="btn btn-sm btn-outline-secondary"
                        onClick={handleDeselectAll}
                      >
                        Clear Selection
                      </button>
                    </div>

                    <div className="d-flex gap-2">
                      {!isChildBranch && (
                        <button
                          className="btn btn-sm btn-primary"
                          onClick={handleBulkEdit}
                          title="Edit Selected Categories"
                        >
                          <i className="fa fa-edit me-1"></i>
                          Edit
                        </button>
                      )}
                      <button
                        className="btn btn-sm btn-danger"
                        onClick={handleBulkDelete}
                        title="Delete Selected Categories"
                      >
                        <i className="fa fa-trash me-1"></i>
                        Delete
                      </button>
                    </div>
                  </div>
                </div>
              )}

              <div
                className="table-responsive mt-3"
                style={{
                  minHeight: "200px",
                  maxHeight: "450px",
                  overflowY: "auto",
                  overflowX: "auto",
                }}
              >
                <table className="table table-hover">
                  <thead
                    className="bg-light"
                    style={{
                      position: "sticky",
                      top: 0,
                      zIndex: 5,
                      backgroundColor: isDarkMode ? "#2d3436" : "#f8f9fa",
                      color: isDarkMode ? "#ffffff" : "#000000",
                    }}
                  >
                    <tr>
                      <th className="pt-2 pb-2">
                        <input
                          type="checkbox"
                          onChange={handleSelectAll}
                          checked={
                            categories.length > 0 &&
                            selectedItems.size === categories.length
                          }
                          disabled={categories.length === 0}
                        />
                      </th>
                      <th className="pt-2 pb-2">Name</th>
                      {visibleColumns.sortOrder && (
                        <th className="pt-2 pb-2 text-center">Sort Order</th>
                      )}
                      <th className="pt-2 pb-2 text-center">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {categories.length > 0 ? (
                      categories.map((category, index) => (
                        <tr
                          key={category.rC_ID}
                          draggable={false}
                          onDragStart={(e) => {
                            // Only allow dragging from the grab icon
                            if (!e.target.classList.contains('fa-bars')) {
                              e.preventDefault();
                            }
                          }}
                          onDragEnter={(e) => handleDragEnter(e, index)}
                          onDragLeave={handleDragLeave}
                          onDragOver={handleDragOver}
                          onDrop={handleDrop}
                        >
                          <td className="align-middle">
                            <input
                              type="checkbox"
                              checked={selectedItems.has(category.rC_ID)}
                              onChange={(e) => handleSelectItem(category.rC_ID, index, e)}
                              data-chkbox-shiftsel="type1"
                            />
                          </td>
                          <td
                            className="align-middle"
                            onClick={() => {
                              if (isChildBranch) return;
                              handleEditCategoryClick(category);
                            }}
                            style={{ cursor: isChildBranch ? "default" : "pointer" }}
                          >
                            {category.rC_Name}
                          </td>
                          {visibleColumns.sortOrder && (
                            <td className="align-middle text-center">
                              {category.rC_SortOrder}
                            </td>
                          )}
                          <td className="align-middle text-center">
                            <div className="d-flex justify-content-center gap-2">
                              {/* Drag Handle */}
                              <i
                                className="fas fa-bars text-secondary"
                                style={{ 
                                  cursor: "grab",
                                  width: '32px',
                                  height: '32px',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  borderRadius: '6px',
                                  border: '1px solid #6c757d',
                                  backgroundColor: 'transparent',
                                  transition: 'all 0.2s ease'
                                }}
                                draggable
                                onDragStart={(e) => handleDragStart(e, category)}
                                onDragEnd={handleDragEnd}
                                title="Drag to reorder"
                                onMouseEnter={(e) => {
                                  e.currentTarget.style.backgroundColor = '#6c757d';
                                  e.currentTarget.style.color = 'white';
                                  e.currentTarget.style.transform = 'scale(1.05)';
                                }}
                                onMouseLeave={(e) => {
                                  e.currentTarget.style.backgroundColor = 'transparent';
                                  e.currentTarget.style.color = '#6c757d';
                                  e.currentTarget.style.transform = 'scale(1)';
                                }}
                              ></i>

                              {/* Edit Icon */}
                              {!isChildBranch && (
                                <button
                                  onClick={() => handleEditCategoryClick(category)}
                                  className="btn btn-sm btn-outline-primary"
                                  title="Edit Category"
                                  style={{
                                    width: '32px',
                                    height: '32px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    borderRadius: '6px',
                                    border: `1px solid ${themeColor}`,
                                    color: themeColor,
                                    transition: 'all 0.2s ease'
                                  }}
                                  onMouseEnter={(e) => {
                                    e.currentTarget.style.backgroundColor = themeColor;
                                    e.currentTarget.style.color = 'white';
                                    e.currentTarget.style.transform = 'scale(1.05)';
                                  }}
                                  onMouseLeave={(e) => {
                                    e.currentTarget.style.backgroundColor = 'transparent';
                                    e.currentTarget.style.color = themeColor;
                                    e.currentTarget.style.transform = 'scale(1)';
                                  }}
                                >
                                  <i className="fa fa-edit" style={{ fontSize: '12px' }}></i>
                                </button>
                              )}

                              {/* Delete Icon */}
                              {!isChildBranch && (
                                <button
                                  onClick={() => DeleteCategory(category)}
                                  className="btn btn-sm btn-outline-danger"
                                  title="Delete Category"
                                  style={{
                                    width: '32px',
                                    height: '32px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    borderRadius: '6px',
                                    border: '1px solid #dc3545',
                                    color: '#dc3545',
                                    transition: 'all 0.2s ease'
                                  }}
                                  onMouseEnter={(e) => {
                                    e.currentTarget.style.backgroundColor = '#dc3545';
                                    e.currentTarget.style.color = 'white';
                                    e.currentTarget.style.transform = 'scale(1.05)';
                                  }}
                                  onMouseLeave={(e) => {
                                    e.currentTarget.style.backgroundColor = 'transparent';
                                    e.currentTarget.style.color = '#dc3545';
                                    e.currentTarget.style.transform = 'scale(1)';
                                  }}
                                >
                                  <i className="fa fa-trash" style={{ fontSize: '12px' }}></i>
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        {/* <td colSpan={visibleColumns.sortOrder ? 5 : 4} className="text-center py-4">
                          No category types found. Click "Add Category Type" to create one.
                        </td> */}
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>

      <CategoryTypeModel
        isModalOpen={showAddCategoryModal}
        setIsModalOpen={setShowAddCategoryModal}
        onCategorySubmit={handleCategorySubmit}
        compID={compID}
        categoryToEdit={categoryToEdit}
      />
    </div>
  );
};

export default ManageCategoryTypes;